import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { HERO } from "@/constants/testIds";

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function Hero() {
  return (
    <section
      id="hero"
      data-testid={HERO.root}
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-end">
        <div className="md:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="caption mb-6"
          >
            Custom Solutions Studio — Mumbai, India
          </motion.div>

          <h1 className="font-serif-display text-[44px] leading-[1.02] sm:text-6xl md:text-7xl lg:text-[88px] tracking-tighter text-[#1A1A1A]">
            <motion.span
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.05 }}
              className="block"
            >
              Build AI Solutions,
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.18 }}
              className="block italic font-light"
            >
              CRM Systems &
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.32 }}
              className="block"
            >
              Custom Software Development.
            </motion.span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-8 max-w-xl text-[#6B6A68] text-base md:text-lg leading-relaxed"
          >
            We design and build custom mobile apps, CRMs, web platforms, AI agents
            and workflow automations for ambitious founders and modern enterprises.
            One studio. Every layer of your product.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.65 }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <button
              data-testid={HERO.primaryCta}
              onClick={() => scrollToId("contact")}
              className="group relative inline-flex items-center gap-3 px-8 md:px-9 py-5 md:py-[22px] rounded-full bg-[#1A1A1A] text-[#FDFCFB] text-[15px] md:text-base font-medium tracking-tight shadow-[0_18px_40px_-12px_rgba(0,0,0,0.5)] hover:shadow-[0_24px_50px_-12px_rgba(0,0,0,0.6)] hover:-translate-y-0.5 transition-all duration-300"
            >
              <span className="w-2 h-2 rounded-full bg-[#7EE787] shadow-[0_0_10px_rgba(126,231,135,0.8)] animate-pulse" />
              Book a Free Consultation
              <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid={HERO.secondaryCta}
              onClick={() => scrollToId("what-we-build")}
              className="text-sm text-[#1A1A1A] underline underline-offset-[6px] decoration-[#1A1A1A]/40 hover:decoration-[#1A1A1A] transition-all"
            >
              See what we build
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.85 }}
            className="mt-5 flex items-center gap-4 text-[11px] tracking-[0.18em] uppercase font-mono-tech text-[#6B6A68]"
          >
            <span>30-min call</span>
            <span className="w-1 h-1 rounded-full bg-[#6B6A68]/40" />
            <span>No obligation</span>
            <span className="w-1 h-1 rounded-full bg-[#6B6A68]/40" />
            <span>Reply within 24h</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: [0.2, 0.6, 0.2, 1] }}
          className="md:col-span-5"
        >
          <div className="relative w-full aspect-[3/4] md:aspect-[4/5] overflow-hidden rounded-sm bg-[#F5F4F0]">
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80"
              alt="Analytics dashboard on a laptop"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-[11px] tracking-[0.18em] uppercase text-[#FDFCFB]/90 mix-blend-difference font-mono-tech">
              <span>Est. 2025</span>
              <span>Vol. 01</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Trust strip */}
      <div className="mt-20 md:mt-28 border-y border-[#E8E6E1] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex items-center gap-12 overflow-hidden whitespace-nowrap">
          <div className="flex items-center gap-12 marquee-track shrink-0">
            {[
              "Mobile Apps",
              "CRMs",
              "Websites",
              "AI Agents",
              "Chatbots",
              "SaaS MVPs",
              "Automation",
              "Dashboards",
              "E-commerce",
              "Strategy",
              "Mobile Apps",
              "CRMs",
              "Websites",
              "AI Agents",
              "Chatbots",
              "SaaS MVPs",
              "Automation",
              "Dashboards",
              "E-commerce",
              "Strategy",
            ].map((t, i) => (
              <span
                key={i}
                className="font-serif-display italic text-2xl md:text-3xl text-[#1A1A1A]/80"
              >
                {t} <span className="text-[#1A1A1A]/20 not-italic ml-12">/</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
